import { PERIODS, tspLimit, matchPct, employeePct } from "@/lib/finance";

const iso = (d) => d.toISOString().slice(0, 10);
const utc = (y, m, d) => new Date(Date.UTC(y, m, d));

export function payDates(year, freq, firstPayDate) {
  if (freq === "monthly") return [...Array(12)].map((_, m) => iso(utc(year, m + 1, 0)));
  if (freq === "semimonthly") return [...Array(12)].flatMap((_, m) => [iso(utc(year, m, 15)), iso(utc(year, m + 1, 0))]);
  // Bi-weekly: anchor on the user's first pay date (any year), else the first Friday of January.
  let anchor = firstPayDate ? new Date(`${firstPayDate}T00:00:00Z`) : null;
  if (!anchor || isNaN(anchor)) {
    anchor = utc(year, 0, 1);
    while (anchor.getUTCDay() !== 5) anchor = utc(year, 0, anchor.getUTCDate() + 1);
  }
  const step = 14 * 86400000;
  let t = anchor.getTime();
  const start = utc(year, 0, 1).getTime(), end = utc(year + 1, 0, 1).getTime();
  t += Math.ceil((start - t) / step) * step;
  const out = [];
  for (; t < end; t += step) out.push(iso(new Date(t)));
  return out;
}

export function buildLedger(p, year, overrides = []) {
  const dates = payDates(year, p.pay_frequency, p.first_pay_date);
  const n = PERIODS[p.pay_frequency];
  const gross = (Number(p.salary) || 0) / n;
  const ageThisYear = Number(p.age) || 0;
  const cap = tspLimit(ageThisYear);
  const plannedPct = employeePct(p, Number(p.salary) || 0);
  const planned = p.contrib_mode === "fixed" ? Number(p.contrib_value) || 0 : (gross * plannedPct) / 100;
  const matched = p.retirement_system === "FERS" || p.retirement_system === "BRS";
  const ov = Object.fromEntries(overrides.map((o) => [o.pay_date, o]));
  let cumEmp = 0, cumAgency = 0, cumActEmp = 0, cumActAgency = 0;
  return dates.map((d, i) => {
    const employee = Math.max(0, Math.min(planned, cap - cumEmp));
    const pctThis = gross ? (employee / gross) * 100 : 0;
    const auto = matched ? gross * 0.01 : 0;
    const match = matched ? (gross * (matchPct(p.retirement_system, pctThis) - 1)) / 100 : 0;
    cumEmp += employee;
    cumAgency += auto + match;
    const o = ov[d] || {};
    const actEmp = o.actual_employee ?? null, actAg = o.actual_agency ?? null;
    cumActEmp += actEmp ?? employee;
    cumActAgency += actAg ?? auto + match;
    const roth = (Number(p.roth_split) || 0) / 100;
    return {
      n: i + 1, date: d, gross, employee, trad: employee * (1 - roth), roth: employee * roth, auto, match,
      agency: auto + match, cumEmp, cumAgency, actual_employee: actEmp, actual_agency: actAg,
      variance: (actEmp != null ? actEmp - employee : 0) + (actAg != null ? actAg - (auto + match) : 0),
      verified: !!o.verified, note: o.note || "", capped: employee < planned,
    };
  });
}
